import SearchIcon from '@lmt/unicorn-shared/app/icons/search.svg?react';
import CrossIcon from '@lmt/unicorn-shared/app/icons/search-cross.svg?react';

import React, {
  ChangeEvent,
  ChangeEventHandler,
  FocusEventHandler,
  Ref,
  useCallback,
  useMemo,
  useState,
} from 'react';
import clsx from 'clsx';
import { useCombinedRefs } from '@lmt/unicorn-shared/app/helpers/useCombinedRefs';

import styles from './SearchInput.module.scss';

export interface SearchInputProps {
  id?: string;
  containerRef?: Ref<HTMLDivElement>;
  className?: string;
  searchClassName?: string;
  placeholder?: string;
  value?: string | null;
  onClick?: (e: React.MouseEvent<HTMLInputElement>) => void;
  onEnter?: (text: string) => void;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  onFocus?: FocusEventHandler<HTMLInputElement>;
  bordered?: boolean;
  onSearchIconClick?: (text: string) => void;
  disabled?: boolean;
  initialValue?: string | null;
  allowAnySearch?: boolean;
  blurOnEnter?: boolean;
  onClear?: (text: string) => void;
}
const minCharToSearch = 3;
export const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
  function SearchInput(props, ref) {
    const {
      id,
      containerRef,
      searchClassName,
      className,
      onBlur,
      onChange,
      onClick,
      onEnter,
      placeholder,
      value,
      onFocus,
      bordered,
      onSearchIconClick,
      disabled,
      initialValue,
      allowAnySearch,
      blurOnEnter,
    } = props;

    const isControlled = useMemo(() => value !== undefined, []);
    const [internalValue, setInternalValue] = useState<string>(
      initialValue ?? '',
    );
    const actualValue = useMemo(
      () => (isControlled ? value || '' : internalValue),
      [isControlled, value, internalValue],
    );

    const inputRef = React.useRef<HTMLInputElement>(null);
    const combinedRef = useCombinedRefs<HTMLInputElement>(ref, inputRef);

    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLInputElement>) => {
        const value = actualValue.trim();
        if (event.key === 'Enter') {
          if (
            !allowAnySearch &&
            (!value || (value.length < minCharToSearch && isNaN(Number(value))))
          ) {
            return;
          }
          onEnter?.(actualValue);
          if (blurOnEnter ?? true) {
            inputRef.current?.blur();
          }
        }
      },
      [onEnter, actualValue],
    );

    const handleSearchIconClick = useCallback(() => {
      const value = actualValue.trim();
      if (
        !allowAnySearch &&
        (!value || (value.length < minCharToSearch && isNaN(Number(value))))
      ) {
        return;
      }
      onSearchIconClick?.(actualValue);
      inputRef.current?.blur();
    }, [onSearchIconClick, actualValue]);

    const clearSearchText = useCallback(() => {
      setInternalValue('');
      inputRef.current!.focus();
      inputRef.current!.value = '';
      props.onClear?.(inputRef.current!.value);
      if (isControlled) {
        props.onChange?.({
          target: inputRef.current!,
          currentTarget: inputRef.current!,
        } as any);
      }
    }, [onChange, setInternalValue]);

    const onChangeInputValue = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        const newValue = e.currentTarget.value;
        setInternalValue(newValue);
        onChange?.(e);
      },
      [onChange, setInternalValue],
    );

    const handleOnClick = useCallback(
      (e: React.MouseEvent<HTMLInputElement>) => {
        inputRef.current?.focus();
        onClick?.(e);
      },
      [onClick],
    );

    return (
      <div
        id={id}
        ref={containerRef}
        className={clsx(
          styles.search,
          searchClassName,
          bordered && styles.bordered,
        )}
      >
        <input
          ref={combinedRef}
          className={clsx(styles.input, className)}
          placeholder={placeholder}
          value={actualValue}
          onChange={onChangeInputValue}
          onKeyDown={handleKeyDown}
          onBlur={onBlur}
          onFocus={onFocus}
          onClick={handleOnClick}
          disabled={disabled}
          data-test-id="globalSearchField"
        />
        <div className={styles.iconsContainer}>
          <CrossIcon
            className={
              actualValue.length === 0 ? styles.hidden : styles.crossIcon
            }
            onClick={clearSearchText}
            data-test-id="search-field-cross-icon"
          />
          <SearchIcon
            className={styles.searchIcon}
            onClick={handleSearchIconClick}
            data-test-id="globalSearchIcon"
          />
        </div>
      </div>
    );
  },
);
